"use client"

import { Heading, Text } from "@chakra-ui/react"
import { Box } from "@chakra-ui/react/box"
import { Flex } from "@chakra-ui/react/flex"
import { LinkBox, LinkOverlay } from "@chakra-ui/react/link"
import { HStack, VStack } from "@chakra-ui/react/stack"

import MarkdownParser from "@/components/MarkdownParser"
import { Experience } from "@/types/getAbout"

import AccordionConsultingProjectCard from "./AccordionConsultingProjectCard"
import { GlobeIcon, LaptopIcon, MapPinIcon } from "@/components/icons"

interface AccordionExperienceContentProps {
  experience: Experience
}

const AccordionExperienceContent = ({
  experience,
}: AccordionExperienceContentProps) => {
  const hasConsultingProjects =
    experience.consulting_projects !== undefined &&
    experience.consulting_projects.length > 0

  return (
    <VStack
      alignItems="stretch"
      borderColor={"border.muted"}
      borderTopWidth="1px"
    >
      <HStack
        borderBottomWidth="1px"
        borderColor="border.muted"
        justifyContent={"space-between"}
        marginBottom={2}
        my={2}
        paddingBottom={2}
        paddingLeft={4}
        paddingRight={4}
      >
        <LinkBox
          _hover={{
            _dark: {
              borderColor: "primary.200",
            },
            borderColor: "primary.500",
          }}
          borderBottomWidth="1px"
          borderColor={"transparent"}
        >
          <LinkOverlay
            _dark={{
              color: "primary.200",
            }}
            alignContent="center"
            alignItems="center"
            color="primary.500"
            display={"flex"}
            flexDirection="row"
            flexWrap="wrap"
            href={experience.company.url}
            justifyContent="flex-start"
            padding="0"
            rel="noopener"
            title={experience.company.name + " Current Website"}
          >
            <Text color={"currentColor"} fontSize="xs">
              Website
            </Text>
            <Box color={"currentColor"} display="inline-flex" marginLeft={1}>
              <GlobeIcon boxSize="12px" />
            </Box>
          </LinkOverlay>
        </LinkBox>

        <Flex gap={2}>
          {experience.remote === true && (
            <Flex alignItems="center">
              <Text
                alignItems={"center"}
                color="fg.muted"
                display={"inline-flex"}
                fontSize="xs"
                marginLeft={1}
              >
                <LaptopIcon boxSize="12px" style={{ marginRight: "4px" }} />
                Remote
              </Text>
            </Flex>
          )}

          <Flex alignItems="center">
            <Text
              alignItems={"center"}
              color="fg.muted"
              display={"inline-flex"}
              fontSize="xs"
            >
              <MapPinIcon boxSize="12px" style={{ marginRight: "4px" }} />
              {experience.location}
            </Text>
          </Flex>
        </Flex>
      </HStack>
      <Box
        color={"fg"}
        paddingLeft={4}
        paddingRight={4}
        pb={hasConsultingProjects ? 1 : 4}
      >
        <MarkdownParser
          fontSize="sm"
          marginBottom={"0.5rem"}
          markdown={experience.description}
        />
      </Box>
      <VStack
        alignItems="stretch"
        display={hasConsultingProjects ? "flex" : "none"}
        mb={4}
        paddingLeft={4}
        paddingRight={4}
      >
        <Heading
          color="fg.muted"
          lineHeight={{ base: "1.5" }}
          size={["xs", "sm", "sm"]}
          textAlign="left"
        >
          Consulted on and delivered the following projects as part of my role
          at {experience.company.name}
        </Heading>
        {experience.consulting_projects?.map((project) => (
          <AccordionConsultingProjectCard key={project.id} project={project} />
        ))}
      </VStack>
    </VStack>
  )
}

export default AccordionExperienceContent
